import {
  Button,
  Dimmer,
  DimmerDimmable,
  Dropdown,
  Form,
  Label,
  Loader,
  Message,
  Modal,
  Segment,
  SingleDatePicker,
} from '@mairistem/composants';
import { request } from '@mairistem/tools';
import dayjs from 'dayjs';
import _ from 'lodash';
import React from 'react';
import { MOTIFS_REJET } from '@/constants';
import { getDateLimiteMfoRecu } from '@/routes';
import type { DateLimiteMfoType } from '@/types';

export interface ModalRejetPrelevementRecuProps {
  identifiantRecu: number;
  /** Rejet déjà constaté par l'interrogation MFO : MFO n'est pas rappelé, pas de date. */
  dejaRejete: boolean;
  loading: boolean;
  /** Message d'échec du back ; la modale reste ouverte pour un nouvel essai. */
  erreur?: string | null;
  onValidate: (rejet: { motifRejet: string; dateLimitePaiement: string | null }) => void;
  onClose: () => void;
}

const ModalRejetPrelevementRecu = (props: ModalRejetPrelevementRecuProps) => {
  const { identifiantRecu, dejaRejete, loading, erreur, onValidate, onClose } = props;

  const [motif, setMotif] = React.useState<string | null>(null);
  const [dateLimite, setDateLimite] = React.useState<dayjs.Dayjs | null>(null);
  const [dateActuelle, setDateActuelle] = React.useState<DateLimiteMfoType | null>(null);

  // Date limite actuelle chez MFO, à titre informatif : un échec ou un MFO muet n'affiche rien et
  // ne bloque jamais le rejet. Sans objet si le rejet est déjà connu de MFO.
  React.useEffect(() => {
    if (dejaRejete) {
      return;
    }

    request
      .get(getDateLimiteMfoRecu(identifiantRecu))
      .then((data: DateLimiteMfoType) => setDateActuelle(data))
      .catch(() => setDateActuelle(null));
  }, [dejaRejete, identifiantRecu]);

  const options = React.useMemo(
    () =>
      _.map(MOTIFS_REJET, (libelle, cle) => ({
        key: `rejet-${cle}`,
        value: cle,
        text: libelle,
      })),
    [],
  );

  const handleValidate = React.useCallback(() => {
    onValidate({
      // Le libellé, et non la clé : il est repris tel quel dans le motif d'annulation du reçu.
      motifRejet: MOTIFS_REJET[motif],
      dateLimitePaiement: !dejaRejete && dateLimite ? dateLimite.format('YYYY-MM-DD') : null,
    });
  }, [dateLimite, dejaRejete, motif, onValidate]);

  const dateActuelleConnue =
    dateActuelle?.mfoInterroge && !_.isNil(dateActuelle.dateLimiteReglement);

  const paiementEnLigne = !_.isNil(dateLimite)
    ? `et en ligne jusqu'au ${dateLimite.format('DD/MM/YYYY')}`
    : "et en ligne jusqu'à sa date limite actuelle, si elle n'est pas dépassée";

  return (
    <Modal open size="tiny" {...(!loading && { closeIcon: true, onClose })}>
      <Modal.Header>Rejet du prélèvement</Modal.Header>
      <Modal.Content scrolling={false}>
        {!_.isEmpty(erreur) && (
          <Segment basic style={{ marginLeft: '-0.5em', padding: 0 }}>
            <Label color="red" ribbon data-testid="erreur-rejet-prelevement-recu">
              {erreur}
            </Label>
          </Segment>
        )}
        <DimmerDimmable as={Form} dimmed={loading}>
          <Dimmer active={loading} inverted>
            <Loader inverted content="Chargement" />
          </Dimmer>
          <Form.Field required>
            <label>Motif de rejet</label>
            <Dropdown
              data-testid="combobox-motif-rejet-prelevement-recu"
              selection
              disabled={loading}
              options={options}
              value={motif}
              onChange={(e, { value }) => setMotif(value as string)}
              placeholder="Sélectionnez un motif de rejet"
            />
          </Form.Field>
          {dejaRejete ? (
            <Message info data-testid="message-rejet-deja-connu">
              Le rejet est déjà connu de MFO : il ne lui sera pas redéclaré.
            </Message>
          ) : (
            <>
              {dateActuelleConnue && (
                <p data-testid="date-limite-actuelle-mfo">
                  Date limite actuelle de paiement en ligne :{' '}
                  <strong>{dayjs(dateActuelle.dateLimiteReglement).format('DD/MM/YYYY')}</strong>
                </p>
              )}
              {dateActuelleConnue && dateActuelle.depassee && (
                <Message warning data-testid="message-date-limite-depassee">
                  Elle est dépassée : sans nouvelle date, la facture ne sera plus payable qu&apos;au
                  guichet.
                </Message>
              )}
              <Form.Field>
                <label>Nouvelle date limite de paiement en ligne</label>
                <SingleDatePicker
                  data-testid="date-limite-rejet-prelevement-recu"
                  disabled={loading}
                  clearable
                  value={dateLimite}
                  onChange={(val: dayjs.Dayjs) => setDateLimite(val)}
                  disabledDate={(d) => d.isBefore(dayjs().startOf('day'))}
                />
              </Form.Field>
            </>
          )}
          <Message data-testid="message-effets-rejet-prelevement-recu">
            Le reçu sera annulé. La facture redeviendra payable au guichet
            {dejaRejete ? '.' : `, ${paiementEnLigne}. MFO sera informé du rejet.`}
          </Message>
        </DimmerDimmable>
      </Modal.Content>
      <Modal.Actions>
        <Button
          data-testid="button-close-rejet-prelevement-recu"
          onClick={() => onClose()}
          basic
          size="tiny"
          icon="close"
          content="Annuler"
          loading={loading}
          disabled={loading}
        />
        <Button
          data-testid="button-validate-rejet-prelevement-recu"
          negative
          onClick={handleValidate}
          disabled={_.isNil(motif) || loading}
          content="Rejeter le prélèvement"
          icon="checkmark"
          loading={loading}
        />
      </Modal.Actions>
    </Modal>
  );
};

export default ModalRejetPrelevementRecu;
